'use client'

import { useWalletUsage } from '@/hooks/use-wallet-usage'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { EditUsageLimitPopover } from './EditUsageLimitPopover'
import { LimitCardFooter } from './LimitCardFooter'
import { fmtLimitCurrency } from './limit-card-utils'

function progressTone(pct: number, limitState: string): 'green' | 'yellow' | 'red' {
  if (limitState === 'limit_hit' || pct >= 95) return 'red'
  if (pct >= 80) return 'yellow'
  return 'green'
}

export function UsageCard() {
  const walletUsage = useWalletUsage()
  const data = walletUsage.data
  const loading = walletUsage.isLoading && !data
  const budget = data?.limits.monthly_credit_budget ?? null
  const creditsConsumed = data?.currentMonth.creditsConsumed ?? 0
  const pct = budget != null && budget > 0
    ? Math.min((creditsConsumed / budget) * 100, 100)
    : 0
  const tone = progressTone(pct, data?.limitState ?? 'ok')

  return (
    <div className="bv3-bcard">
      <h3>Monthly usage limit</h3>
      <div className="h3sub">Platform AI events for this billing period.</div>

      <div
        role="progressbar"
        aria-valuenow={creditsConsumed}
        aria-valuemax={budget ?? undefined}
        aria-label={
          budget != null
            ? `${fmtLimitCurrency(creditsConsumed)} used of ${fmtLimitCurrency(budget)}`
            : `${fmtLimitCurrency(creditsConsumed)} used this month`
        }
        className={cn('bv3-prog', tone === 'red' && 'over')}
      >
        <div
          style={{
            width: loading ? '0%' : `${Math.max(pct, 0.5)}%`,
            background: tone === 'yellow' ? '#F59E0B' : tone === 'red' ? '#EF4444' : undefined,
          }}
        />
      </div>

      <LimitCardFooter
        amount={fmtLimitCurrency(creditsConsumed)}
        sublabel={
          budget != null
            ? `used of ${fmtLimitCurrency(budget)} this month`
            : 'used this month'
        }
        changeLimitControl={
          <EditUsageLimitPopover
            currentBudget={budget}
            onSaved={async () => {
              await walletUsage.mutate()
            }}
            trigger={
              <Button type="button" variant="outline" size="sm">
                Change limit
              </Button>
            }
          />
        }
      />
    </div>
  )
}
